import React, { useCallback, useEffect, useState } from 'react';
import { Box, Typography, Card, CardContent, Button, Alert, CircularProgress, Chip, Divider } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { listAthleteTeamMemberships, listAthleteRosterRequests, cancelRosterRequest } from '../../services/rosterService';
import { gradeLabel } from '../../utils/footballHistory';

function formatDate(dateStr) {
  return new Date(dateStr).toLocaleDateString();
}

/**
 * Team Memberships — read-only status (Current Team(s) / Pending
 * Request(s) / Previous Membership Period(s) / Past Request(s)) plus a
 * "Join a Team" link out to the Team Join Code flow (012's request_team_
 * roster_join path). This is the V1 onboarding path: a coach generates a
 * team-wide join code, a guardian enters it and requests to join, and a
 * Team Admin/Head Coach approves it — see JoinTeamPage.
 *
 * The OTHER 012 onboarding path — a guardian generating an athlete-
 * specific connection code for a coach to redeem directly
 * (generate_athlete_connection_code / redeem_athlete_connection_code) —
 * is intentionally NOT surfaced here. Per product decision, it's no
 * longer part of the normal V1 UX; the backend RPCs, table, and RLS are
 * untouched and left in place as a fallback / future share-link basis,
 * just not linked to from this screen.
 *
 * Historical (inactive) memberships stay visible here — removal from a
 * roster is never a deletion, and rejoining always requires a fresh
 * approval, so a past membership is exactly that: history, not an undo
 * point.
 *
 * BROWSER-FOUND FIX (C.6 follow-up) — the CURRENT TEAM / Previous
 * Membership Period card used to show `m.team?.grade_age_group`, a
 * TEAM-level classification string (teams.grade_age_group, 009 — e.g.
 * "7th Grade Travel", describing what age/grade group the TEAM itself
 * targets) as if it were the athlete's OWN grade. That's the exact
 * Grade-vs-Division conflation this phase's product rules forbid, just
 * one level more literal: a Team's own target-grade label is not this
 * athlete's actual grade during that specific membership, and the two
 * can legitimately differ (a 6th grader can be rostered on a Team whose
 * own label says "7th Grade"). Both membership sections below now show
 * gradeLabel(m.season_grade) — the real, membership-specific historical
 * fact (047) — instead. Pending/past REQUESTS (further below) are left
 * untouched: a request has no team_athletes row yet, so there is no
 * season_grade to show, and team.grade_age_group there is legitimately
 * describing the team being requested, not attributed to the athlete.
 */
export default function TeamMembershipSection({ athlete }) {
  const navigate = useNavigate();
  const [memberships, setMemberships] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const [cancellingId, setCancellingId] = useState(null);
  const [cancelError, setCancelError] = useState(null);

  const load = useCallback(async () => {
    const [m, r] = await Promise.all([
      listAthleteTeamMemberships(athlete.id),
      listAthleteRosterRequests(athlete.id),
    ]);
    setMemberships(m);
    setRequests(r);
  }, [athlete.id]);

  useEffect(() => {
    load().catch(e => setLoadError(e.message)).finally(() => setLoading(false));
  }, [load]);

  const handleCancelRequest = async (requestId) => {
    setCancelError(null);
    setCancellingId(requestId);
    try {
      await cancelRosterRequest(requestId);
      await load();
    } catch (e) {
      setCancelError(e.message || 'Could not cancel that request.');
    } finally {
      setCancellingId(null);
    }
  };

  const activeMemberships = memberships.filter(m => m.status === 'active');
  const historicalMemberships = memberships.filter(m => m.status !== 'active');
  const pendingRequests = requests.filter(r => r.status === 'pending');
  const pastRequests = requests.filter(r => r.status !== 'pending');

  return (
    <Card sx={{ backgroundColor: 'rgba(10,20,34,0.8)', border: '1px solid #1e2a3a', mt: 3 }}>
      <CardContent>
        <Typography sx={{ fontWeight: 800, mb: 0.5 }}>Team Memberships</Typography>
        <Typography sx={{ color: '#4a6080', fontSize: '0.75rem', mb: 2 }}>
          Which teams {athlete.first_name} is connected to, and their history on past teams.
        </Typography>

        {loadError && <Alert severity="error" sx={{ mb: 2 }}>{loadError}</Alert>}

        {loading ? (
          <Box sx={{ py: 3, textAlign: 'center' }}><CircularProgress size={24} sx={{ color: '#2ea84b' }} /></Box>
        ) : (
          <>
            {memberships.length === 0 && requests.length === 0 ? (
              <Typography sx={{ color: '#6a8aa8', mb: 2.5 }}>Not connected to any team yet.</Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, mb: 2.5 }}>
                {/* Grouped under explicit headers so a rejoin (old inactive
                    row preserved + new active row created — see migration
                    012) never reads as "on this team twice"; each section
                    heading only appears when it has rows. */}
                {activeMemberships.length > 0 && (
                  <Typography sx={{ fontSize: '0.7rem', color: '#6a8aa8', letterSpacing: '0.05em', fontWeight: 700 }}>
                    CURRENT TEAM{activeMemberships.length > 1 ? 'S' : ''}
                  </Typography>
                )}
                {activeMemberships.map(m => (
                  <Box key={m.id} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                    <Box>
                      <Typography sx={{ fontWeight: 700 }}>{m.team?.team_name || 'Team'}</Typography>
                      <Typography sx={{ color: '#6a8aa8', fontSize: '0.78rem' }}>
                        {gradeLabel(m.season_grade) || 'Grade —'} · {m.team?.season}
                      </Typography>
                    </Box>
                    <Chip size="small" label="Active" sx={{ bgcolor: '#0a2a14', color: '#2ea84b', fontWeight: 700 }} />
                  </Box>
                ))}
                {pendingRequests.length > 0 && (
                  <Typography sx={{ fontSize: '0.7rem', color: '#6a8aa8', letterSpacing: '0.05em', fontWeight: 700, mt: activeMemberships.length > 0 ? 1 : 0 }}>
                    PENDING REQUEST{pendingRequests.length > 1 ? 'S' : ''}
                  </Typography>
                )}
                {pendingRequests.map(r => (
                  <Box key={r.id} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                    <Box>
                      <Typography sx={{ fontWeight: 700 }}>{r.team?.team_name || 'Team'}</Typography>
                      <Typography sx={{ color: '#6a8aa8', fontSize: '0.78rem' }}>
                        {r.team?.grade_age_group} · {r.team?.season} · requested {formatDate(r.requested_at)}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip size="small" label="Pending Approval" sx={{ bgcolor: '#2a1405', color: '#f5a623', fontWeight: 700 }} />
                      <Button
                        size="small" disabled={cancellingId === r.id} onClick={() => handleCancelRequest(r.id)}
                        sx={{ textTransform: 'none', color: '#6a8aa8', '&:hover': { color: '#f44336' } }}
                      >
                        {cancellingId === r.id ? <CircularProgress size={14} /> : 'Cancel'}
                      </Button>
                    </Box>
                  </Box>
                ))}
                {historicalMemberships.length > 0 && (
                  <Typography sx={{ fontSize: '0.7rem', color: '#6a8aa8', letterSpacing: '0.05em', fontWeight: 700, mt: (activeMemberships.length > 0 || pendingRequests.length > 0) ? 1 : 0 }}>
                    PREVIOUS MEMBERSHIP PERIOD{historicalMemberships.length > 1 ? 'S' : ''}
                  </Typography>
                )}
                {historicalMemberships.map(m => (
                  <Box key={m.id} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, opacity: 0.65 }}>
                    <Box>
                      <Typography sx={{ fontWeight: 700 }}>{m.team?.team_name || 'Team'}</Typography>
                      <Typography sx={{ color: '#6a8aa8', fontSize: '0.78rem' }}>
                        {gradeLabel(m.season_grade) || 'Grade —'} · {m.team?.season}
                        {m.added_at && ` · joined ${formatDate(m.added_at)}`}
                        {m.removed_at && ` · left ${formatDate(m.removed_at)}`}
                      </Typography>
                    </Box>
                    <Chip size="small" label="Inactive" sx={{ bgcolor: '#131318', color: '#6a8aa8' }} />
                  </Box>
                ))}
                {pastRequests.length > 0 && (
                  <Typography sx={{ fontSize: '0.7rem', color: '#6a8aa8', letterSpacing: '0.05em', fontWeight: 700, mt: (activeMemberships.length > 0 || pendingRequests.length > 0 || historicalMemberships.length > 0) ? 1 : 0 }}>
                    PAST REQUEST{pastRequests.length > 1 ? 'S' : ''}
                  </Typography>
                )}
                {pastRequests.map(r => (
                  <Box key={r.id} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, opacity: 0.65 }}>
                    <Box>
                      <Typography sx={{ fontWeight: 700 }}>{r.team?.team_name || 'Team'}</Typography>
                      <Typography sx={{ color: '#6a8aa8', fontSize: '0.78rem' }}>
                        {r.team?.grade_age_group} · {r.team?.season} · requested {formatDate(r.requested_at)}
                      </Typography>
                    </Box>
                    <Chip
                      size="small"
                      label={r.status === 'declined' ? 'Declined' : r.status === 'cancelled' ? 'Cancelled' : 'Approved'}
                      sx={{ bgcolor: '#131318', color: '#6a8aa8' }}
                    />
                  </Box>
                ))}
              </Box>
            )}
            {cancelError && <Alert severity="error" sx={{ mb: 2 }}>{cancelError}</Alert>}

            <Divider sx={{ mb: 2 }} />

            <Typography sx={{ color: '#4a6080', fontSize: '0.75rem', mb: 1.5 }}>
              To connect {athlete.first_name} to a team, enter the team join code a coach shared with you — you'll pick{' '}
              {athlete.first_name} from your athletes and submit a request the team still has to approve.
            </Typography>
            <Button
              variant="outlined" color="primary" onClick={() => navigate('/athletes/join-team')}
              sx={{ textTransform: 'none', fontWeight: 700 }}
            >
              Join a Team
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  );
}
